// Path helpers shared by the metadata file views (DataSourceDetail folder grouping,
// FolderDetail drill-down page, FolderDiscoverDialog). Pure functions — jest-tested.

/**
 * Directory a file lives directly under — the grouping key for the collapsible file
 * panels. Keeps the trailing slash so the label reads like a folder ("/test-pii-files/").
 * Files with no slash in their path fall into the scan-root group ('').
 */
export function parentDir(p) {
  if (!p) return '';
  const s = String(p);
  const idx = s.lastIndexOf('/');
  return idx >= 0 ? s.slice(0, idx + 1) : '';
}

/**
 * Filename for display inside a group panel — the folder is already in the panel header,
 * so rows only need the leaf name. Accepts a file object ({name, path}) like the
 * profile results carry.
 */
export function baseName(f) {
  if (f.name) return f.name;
  if (!f.path) return '—';
  const s = String(f.path);
  const idx = s.lastIndexOf('/');
  return idx >= 0 ? s.slice(idx + 1) : s;
}

/** Strip leading and trailing slashes. */
export const trimSlashes = (p) => String(p || '').replace(/^\/+|\/+$/g, '');

/**
 * Trailing slash(es) only — keeps '/' for the filesystem root, and preserves the
 * archive boundary marker: '/data/export.zip!/' must NOT become '/data/export.zip!'
 * (the worker's virtual-path routing keys on the two-character '!/' boundary, so a
 * bare trailing '!' reads as a regular file named "export.zip!" that doesn't exist).
 */
export function trimTrailingSlash(p) {
  const s = String(p || '');
  if (s === '/') return s;
  const t = s.replace(/\/+$/, '');
  return t.endsWith('!') ? `${t}/` : t;
}

/**
 * Last segment of a folder path ('/data/2024/' → '2024'). '' for the bare root.
 * A trailing archive boundary is dropped ('/data/export.zip!/' → 'export.zip') so
 * derived data-source names stay clean.
 */
export function folderLeafName(p) {
  const t = trimSlashes(p);
  const idx = t.lastIndexOf('/');
  const leaf = idx >= 0 ? t.slice(idx + 1) : t;
  return leaf.endsWith('!') ? leaf.slice(0, -1) : leaf;
}

/** Escape a literal string for embedding in a regex. */
export const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Anchored alternation matching exactly the given file names — the fileNameFilter a
 * "discover selected files" action saves on the created data source. Case-insensitivity
 * is applied by the worker's Pattern compile, not here.
 */
export const buildFileNameFilter = (names) =>
  `^(?:${(names || []).map(escapeRegex).join('|')})$`;

/**
 * Encode a folder path for a React Router 5 `:folderPath+` segment. Per-segment
 * encodeURIComponent keeps '/' as the segment separator (the `+` param preserves them);
 * '!' survives encodeURIComponent so archive-virtual paths (data.zip!/inner) round-trip.
 *
 * Cloud-scheme paths (s3://, sharepoint://, azblob://) contain an EMPTY segment after
 * the scheme, which breaks the route twice over: path-to-regexp's `+` repeat can't match
 * an empty segment (the page silently fails to render), and nginx 404s a deep-link
 * refresh on an encoded-slash workaround. Empty segments are DROPPED from the URL
 * ('s3://bucket' → 's3:/bucket'); normalizeFolderKey collapses slash runs on both sides
 * of a comparison, so the single-slash decoded form still matches the real paths.
 */
export const encodeFolderPathForUrl = (p) =>
  trimSlashes(p).split('/').filter(Boolean).map(encodeURIComponent).join('/');

/** Inverse of encodeFolderPathForUrl for a `:folderPath+` param value. */
export const decodeFolderParam = (raw) =>
  String(raw || '').split('/').map(decodeURIComponent).join('/');

/**
 * Normalized comparison key: same folder regardless of leading/trailing slashes, with
 * interior slash runs collapsed so a URL-round-tripped cloud path ('s3:/bucket/x')
 * still matches the real one ('s3://bucket/x').
 */
export const normalizeFolderKey = (p) => trimSlashes(p).replace(/\/{2,}/g, '/');
